import { createInvocationId, logTemplateWebMCP } from '../logging';
import { mcpToolResult, toolError, toolSuccess, type TemplateWebMCPToolResult } from '../responses';
import { sanitizeEditorSummary, type TemplateBuilderReadData } from '../sanitize';

const READ_SOURCE_SCHEMA = {
  type: 'object',
  properties: {
    sourceId: { type: 'string' },
    name: { type: 'string' },
  },
  required: ['sourceId', 'name'],
} as const;

const READ_NAME_CONFIG_SCHEMA = {
  type: 'object',
  properties: {
    isEditable: { type: 'boolean' },
    customAsset: {
      anyOf: [
        {
          type: 'object',
          properties: {
            folder: { type: 'string' },
            fileTemplate: { type: 'string' },
          },
          required: ['folder', 'fileTemplate'],
        },
        { type: 'null' },
      ],
    },
  },
  required: ['isEditable'],
} as const;

const READ_DYNAMIC_FONT_SCHEMA = {
  type: 'object',
  properties: {
    enabled: { type: 'boolean' },
    maxFontSize: { type: 'number' },
    minFontSize: { type: 'number' },
    lineHeight: { type: 'number' },
    multiLine: { type: 'boolean' },
  },
  required: ['enabled'],
} as const;

const READ_STYLE_SCHEMA = {
  type: 'object',
  properties: {
    textAlign: { type: ['string', 'null'] },
    fontSize: { type: ['number', 'null'] },
    lineHeight: { type: ['number', 'null'] },
    fontFamily: { type: ['string', 'null'] },
    fontVariant: { type: ['string', 'null'] },
    textCase: { type: ['string', 'null'] },
  },
} as const;

const READ_ELEMENT_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    type: { type: 'string' },
    name: { type: 'string' },
    position: {
      type: 'object',
      properties: { x: { type: 'number' }, y: { type: 'number' } },
      required: ['x', 'y'],
    },
    sources: { type: 'array', items: READ_SOURCE_SCHEMA },
    nameConfig: { anyOf: [READ_NAME_CONFIG_SCHEMA, { type: 'null' }] },
    contentTokens: { type: 'array', items: { type: 'string' } },
    contentTemplate: { type: ['string', 'null'] },
    dynamicFont: { anyOf: [READ_DYNAMIC_FONT_SCHEMA, { type: 'null' }] },
    style: { anyOf: [READ_STYLE_SCHEMA, { type: 'null' }] },
  },
  required: ['id', 'type', 'name', 'position', 'sources', 'contentTokens'],
} as const;

export const TEMPLATE_BUILDER_READ_TOOL = {
  name: 'template_builder_read',
  // Listings over the relay only expose description (and annotations). The output shape lives
  // here rather than on `annotations`, which is restricted to a short title and boolean hints.
  description:
    'Read a sanitized in-memory Template Builder summary for the currently open template, including data-source mappings and text style. Does not persist changes and takes no arguments. Returns { ok, data?, error? }. On success, data holds templateId, name, selectedElementId (nullable), elementCount, dataSources ({ id, type }) and elements. Each element holds id, type, name, position ({ x, y }), sources ({ sourceId, name }), contentTokens, and the nullable nameConfig ({ isEditable, customAsset }), contentTemplate, dynamicFont ({ enabled, maxFontSize, minFontSize, lineHeight, multiLine }) and style ({ textAlign, fontSize, lineHeight, fontFamily, fontVariant, textCase }). On failure, error holds { code, message } where code is UNAUTHORIZED, INVALID_INPUT, EDITOR_NOT_READY or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Read Template Builder',
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {},
    additionalProperties: false,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: {
        type: 'object',
        properties: {
          templateId: { type: 'string' },
          name: { type: 'string' },
          selectedElementId: { type: ['string', 'null'] },
          elementCount: { type: 'integer' },
          dataSources: {
            type: 'array',
            items: {
              type: 'object',
              properties: { id: { type: 'string' }, type: { type: 'string' } },
              required: ['id', 'type'],
            },
          },
          elements: { type: 'array', items: READ_ELEMENT_SCHEMA },
        },
        required: [
          'templateId',
          'name',
          'selectedElementId',
          'elementCount',
          'dataSources',
          'elements',
        ],
      },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

type EditorStoreLike = {
  getState: () => unknown;
};

type ExecuteArgs = {
  store: EditorStoreLike | null;
  templateId: string;
  canEdit: boolean;
  input?: unknown;
  signal?: AbortSignal;
  persist?: (...args: unknown[]) => unknown;
};

function isEmptyInput(input: unknown): boolean {
  if (input == null) {
    return true;
  }
  if (typeof input !== 'object' || Array.isArray(input)) {
    return false;
  }
  return Object.keys(input as Record<string, unknown>).length === 0;
}

export async function executeTemplateBuilderRead({
  store,
  templateId,
  canEdit,
  input,
  signal,
  persist: _persist,
}: ExecuteArgs): Promise<TemplateWebMCPToolResult<TemplateBuilderReadData>> {
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_READ_TOOL.name,
    invocationId,
    input,
  });

  const finish = (
    result: TemplateWebMCPToolResult<TemplateBuilderReadData>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_READ_TOOL.name,
      invocationId,
      outcome,
      durationMs: Date.now() - startedAt,
      output: result.structuredContent,
    });
    return result;
  };

  try {
    if (signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }

    if (!canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }

    if (!isEmptyInput(input)) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_read does not accept arguments.', {
            receivedKeys: Object.keys((input as Record<string, unknown>) ?? {}),
          }),
        ),
        'validation_failure',
      );
    }

    const state = store?.getState() as { name?: unknown; elements?: unknown } | null | undefined;
    const summary = sanitizeEditorSummary(state, templateId);
    if (!store || !state || !summary) {
      return finish(
        mcpToolResult(toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.')),
        'validation_failure',
      );
    }

    void _persist;
    return finish(mcpToolResult(toolSuccess(summary)), 'success');
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
